import { useTheme } from '@mui/material';
import DSBox from '../../../../design-system/DSBox';
import { DSCollectionCoverAvatar } from '../../../../design-system/Collection';
import { CollectionReviewStates } from '../../../../api/collections';
import { DSTypography } from '../../../../design-system/Typography';
import CollectionLabels from './CollectionLabels';
import CollectionMetadata from './CollectionMetadata';
import CollectionStats from '../CollectionStats';

export type CollectionInfoProps = {
  description: string;
  cover: string;
  collectionStatus?: CollectionReviewStates;
  reviewStatusMessage?: string;
  labels?: string[];
  credits?: string;
  klydoCount: number;
  hasChime?: boolean;
  hasSound?: boolean;
  hasThirteenPlusTag?: boolean;
  hasPSFTag?: boolean;
  hasReminder?: boolean;
  totalPurchases?: number;
  totalSales?: number;
  sponsored?: boolean;
  totalRedemptions?: number;
  statsLoading?: boolean;
  statsError?: boolean;
};

const CollectionInfo = ({
  description,
  cover,
  reviewStatusMessage,
  collectionStatus,
  labels,
  credits,
  klydoCount,
  hasChime,
  hasSound,
  hasThirteenPlusTag,
  hasPSFTag,
  hasReminder,
  totalPurchases = 0,
  totalSales = 0,
  sponsored = false,
  totalRedemptions = 0,
  statsLoading = false,
  statsError = false,
}: CollectionInfoProps) => {
  const { spacing, palette } = useTheme();
  const isRejected = collectionStatus === CollectionReviewStates.Rejected;
  const rejectedMessage =
    reviewStatusMessage || 'Your collection has been rejected.';

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(3),
        width: '100%',
      }}
    >
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: { xxs: 'column', sm: 'row' },
          alignItems: { xxs: 'center', sm: 'flex-start' },
          gap: spacing(5),
          width: '100%',
        }}
      >
        <DSCollectionCoverAvatar
          src={cover}
          sx={{
            overflow: 'hidden',
          }}
        />

        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            width: { xxs: '100%', sm: '60%' },
            gap: spacing(2),
          }}
        >
          {isRejected ? (
            <DSTypography variant="body2" color="error">
              {rejectedMessage}
            </DSTypography>
          ) : (
            <CollectionStats
              disabled={collectionStatus === CollectionReviewStates.Pending}
              totalPurchases={totalPurchases}
              totalSales={totalSales}
              sponsored={sponsored}
              totalRedemptions={totalRedemptions}
              isLoading={statsLoading}
              hasError={statsError}
            />
          )}

          <DSBox
            sx={{ display: 'flex', flexDirection: 'row', gap: spacing(5) }}
          >
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing(1),
                width: '100%',
                height: 'auto',
              }}
            >
              <DSTypography
                variant="body1"
                sx={{ color: palette.text.primary }}
              >
                {description}
              </DSTypography>
              {credits && (
                <DSTypography
                  variant="body2"
                  sx={{ color: palette.text.secondary, whiteSpace: 'pre-line' }}
                >
                  {credits}
                </DSTypography>
              )}
            </DSBox>
          </DSBox>
          {labels && labels?.length > 0 && <CollectionLabels labels={labels} />}
          <CollectionMetadata
            klydoCount={klydoCount}
            hasChime={hasChime}
            hasSound={hasSound}
            hasThirteenPlusTag={hasThirteenPlusTag}
            hasPSFTag={hasPSFTag}
            hasReminder={hasReminder}
          />
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default CollectionInfo;
